import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import {
  DragDropModule,
  CdkDragDrop,
  CdkDrag,
  transferArrayItem,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

export interface CalendarReservation {
  id: string;
  patientId: string;
  patientName: string;
  bedId: string;
  roomNumber: string;
  checkInDate: Date;
  checkOutDate: Date;
  packageDays: number;
  therapySessionDate?: Date | null; // Fecha de tratamiento - si existe usa esta, si no calcula según packageDays
  status:
    | 'reserved'
    | 'confirmed'
    | 'in_progress'
    | 'completed'
    | 'canceled'
    | 'draft'
    | 'checked_in'
    | 'no_show';
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  reservations: CalendarReservation[];
}

export interface ReservationMoveEvent {
  reservation: CalendarReservation;
  previousDate: Date;
  newDate: Date;
  newCheckInDate: Date;
}

@Component({
  selector: 'lib-reservations-calendar',
  standalone: true,
  imports: [CommonModule, TranslateModule, Button, Tooltip, DragDropModule],
  templateUrl: './reservations-calendar.component.html',
  styleUrls: ['./reservations-calendar.component.css'],
})
export class ReservationsCalendarComponent implements OnInit, OnChanges {
  @Input() reservations: CalendarReservation[] = [];
  @Input() maxBedsPerDay = 2; // Máximo de camas por día (configurable)
  @Output() reservationClick = new EventEmitter<CalendarReservation>();
  @Output() dateClick = new EventEmitter<Date>();
  @Output() reservationMove = new EventEmitter<ReservationMoveEvent>(); // Nuevo evento para drag-and-drop

  currentDate = new Date();
  currentMonth = this.currentDate.getMonth();
  currentYear = this.currentDate.getFullYear();

  calendarDays: CalendarDay[] = [];
  weekDays = [
    'common.weekdays.sunday',
    'common.weekdays.monday',
    'common.weekdays.tuesday',
    'common.weekdays.wednesday',
    'common.weekdays.thursday',
    'common.weekdays.friday',
    'common.weekdays.saturday',
  ];

  monthNames = [
    'common.months.january',
    'common.months.february',
    'common.months.march',
    'common.months.april',
    'common.months.may',
    'common.months.june',
    'common.months.july',
    'common.months.august',
    'common.months.september',
    'common.months.october',
    'common.months.november',
    'common.months.december',
  ];

  // IDs de las listas conectadas para drag-and-drop
  connectedDropLists: string[] = [];

  ngOnInit(): void {
    this.generateCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reservations']) {
      this.generateCalendar();
    }
  }

  generateCalendar(): void {
    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
    const prevLastDay = new Date(this.currentYear, this.currentMonth, 0);

    const firstDayIndex = firstDay.getDay();
    const lastDayDate = lastDay.getDate();
    const prevLastDayDate = prevLastDay.getDate();

    this.calendarDays = [];

    // Previous month days
    for (let i = firstDayIndex; i > 0; i--) {
      const date = new Date(this.currentYear, this.currentMonth - 1, prevLastDayDate - i + 1);
      this.calendarDays.push({
        date,
        isCurrentMonth: false,
        isToday: this.isToday(date),
        reservations: this.getReservationsForDate(date),
      });
    }

    // Current month days
    for (let i = 1; i <= lastDayDate; i++) {
      const date = new Date(this.currentYear, this.currentMonth, i);
      this.calendarDays.push({
        date,
        isCurrentMonth: true,
        isToday: this.isToday(date),
        reservations: this.getReservationsForDate(date),
      });
    }

    // Next month days
    const remainingDays = 42 - this.calendarDays.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(this.currentYear, this.currentMonth + 1, i);
      this.calendarDays.push({
        date,
        isCurrentMonth: false,
        isToday: this.isToday(date),
        reservations: this.getReservationsForDate(date),
      });
    }

    // Generar IDs para los drop lists (conectar todos los días entre sí)
    this.connectedDropLists = this.calendarDays.map((_, index) => `day-drop-${index}`);
  }

  getReservationsForDate(date: Date): CalendarReservation[] {
    return this.reservations.filter((reservation) => {
      const checkIn = new Date(reservation.checkInDate);

      // Normalize dates to compare only day/month/year
      checkIn.setHours(0, 0, 0, 0);
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);

      let treatmentDate: Date;

      // Si existe therapySessionDate del API, usar esa fecha (fue modificada manualmente)
      if (reservation.therapySessionDate) {
        treatmentDate = new Date(reservation.therapySessionDate);
        treatmentDate.setHours(0, 0, 0, 0);
      } else {
        // Si no existe, calcular según el paquete
        const treatmentDayMap: Record<number, number> = {
          7: 4, // Paquete 7 días → Tratamiento día 4
          10: 4, // Paquete 10 días → Tratamiento día 4
          14: 8, // Paquete 14 días → Tratamiento día 8
          35: 28, // Paquete 35 días → Tratamiento día 28
        };

        const treatmentDayNumber = treatmentDayMap[reservation.packageDays];

        if (!treatmentDayNumber) {
          // Si no hay día de tratamiento definido para este paquete, no mostrar
          return false;
        }

        // Calcular la fecha exacta del día de tratamiento
        treatmentDate = new Date(checkIn);
        treatmentDate.setDate(checkIn.getDate() + (treatmentDayNumber - 1));
        treatmentDate.setHours(0, 0, 0, 0);
      }

      // Solo mostrar en el día específico de tratamiento
      return compareDate.getTime() === treatmentDate.getTime();
    });
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  previousMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendar();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendar();
  }

  today(): void {
    const now = new Date();
    this.currentMonth = now.getMonth();
    this.currentYear = now.getFullYear();
    this.generateCalendar();
  }

  onReservationClick(reservation: CalendarReservation, event: Event): void {
    event.stopPropagation();
    this.reservationClick.emit(reservation);
  }

  onDateClick(day: CalendarDay): void {
    this.dateClick.emit(day.date);
  }

  getStatusColor(_status: string): string {
    // Morado primary para todos los tratamientos
    return '#6366f1';
  }

  getReservationPosition(
    _reservation: CalendarReservation,
    _day: CalendarDay,
  ): 'start' | 'middle' | 'end' | 'single' {
    // Como solo mostramos días individuales (días de tratamiento), todas las cards son 'single'
    return 'single';
  }

  shouldShowReservationLabel(_reservation: CalendarReservation, _day: CalendarDay): boolean {
    // Siempre mostrar el label ya que solo mostramos días individuales
    return true;
  }

  // ========== DRAG AND DROP ==========

  /**
   * Obtiene el ID del drop list para un día específico
   */
  getDropListId(index: number): string {
    return `day-drop-${index}`;
  }

  /**
   * Verifica si un día puede aceptar más reservaciones (máximo 2 camas)
   */
  canDropOnDay(day: CalendarDay): boolean {
    return day.reservations.length < this.maxBedsPerDay;
  }

  /**
   * Predicado para determinar si se puede soltar una reservación en un día
   */
  canDropPredicate = (day: CalendarDay) => {
    return (_drag: CdkDrag<CalendarReservation>): boolean => {
      return day.reservations.length < this.maxBedsPerDay;
    };
  };

  /**
   * Maneja el evento de soltar una reservación en un día
   */
  onDrop(event: CdkDragDrop<CalendarReservation[]>, targetDay: CalendarDay): void {
    if (event.previousContainer === event.container) {
      // Movimiento dentro del mismo día - solo reordenar
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      // Movimiento entre días diferentes
      const reservation = event.previousContainer.data[event.previousIndex];

      // Verificar límite de camas antes de mover
      if (targetDay.reservations.length >= this.maxBedsPerDay) {
        console.warn(
          `❌ No se puede mover: el día ${targetDay.date.toLocaleDateString()} ya tiene ${
            this.maxBedsPerDay
          } camas ocupadas`,
        );
        return;
      }

      // Obtener la fecha anterior (día de tratamiento)
      const previousDay = this.calendarDays.find((d) =>
        d.reservations.some((r) => r.id === reservation.id),
      );

      if (!previousDay) {
        console.error('No se encontró el día anterior de la reservación');
        return;
      }

      // Calcular el nuevo check-in basado en el nuevo día de tratamiento
      const newCheckInDate = this.calculateNewCheckInDate(reservation, targetDay.date);

      // Transferir la reservación al nuevo día
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );

      // Emitir evento con la información del movimiento
      const moveEvent: ReservationMoveEvent = {
        reservation: reservation,
        previousDate: previousDay.date,
        newDate: targetDay.date,
        newCheckInDate: newCheckInDate,
      };

      console.log('📅 Reservación movida:', {
        paciente: reservation.patientName,
        fechaAnterior: previousDay.date.toLocaleDateString(),
        nuevaFecha: targetDay.date.toLocaleDateString(),
        nuevoCheckIn: newCheckInDate.toLocaleDateString(),
      });

      this.reservationMove.emit(moveEvent);
    }
  }

  /**
   * Calcula la nueva fecha de check-in basada en el nuevo día de tratamiento
   */
  private calculateNewCheckInDate(reservation: CalendarReservation, newTreatmentDate: Date): Date {
    // Mapa de días de tratamiento según el paquete
    const treatmentDayMap: Record<number, number> = {
      7: 4, // Paquete 7 días → Tratamiento día 4
      10: 4, // Paquete 10 días → Tratamiento día 4
      14: 8, // Paquete 14 días → Tratamiento día 8
      35: 28, // Paquete 35 días → Tratamiento día 28
    };

    const treatmentDayNumber = treatmentDayMap[reservation.packageDays] || 4;

    // El nuevo check-in es: fecha de tratamiento - (día de tratamiento - 1)
    const newCheckIn = new Date(newTreatmentDate);
    newCheckIn.setDate(newTreatmentDate.getDate() - (treatmentDayNumber - 1));

    return newCheckIn;
  }

  /**
   * Obtiene el número de camas disponibles para un día
   */
  getAvailableBeds(day: CalendarDay): number {
    return Math.max(0, this.maxBedsPerDay - day.reservations.length);
  }

  /**
   * Verifica si un día está lleno
   */
  isDayFull(day: CalendarDay): boolean {
    return day.reservations.length >= this.maxBedsPerDay;
  }
}
